import { Component, Input, OnInit, inject } from "@angular/core";
import { LinkedAssessmentModel } from "@app/assessment/shared/models";
import { RendererType } from "@enums/RendererType";
import { FormatType } from "@enums/FormatType";
import { GenericListConfig } from "@app/shared/models/GenericList/GenericList";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { TabService } from "@services/tabs/TabService";
import { Pages } from "@constants/pages/Pages";
import { AssessmentDataService } from "@app/assessment/services/assessment-data.service";
import { FindingReportDataService } from "@app/findingreport/services";
import { finalize, map } from "rxjs/operators";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { ToastrService } from "ngx-toastr";
import { firstValueFrom } from "rxjs";
import { SharedModule } from "@app/shared/shared.module";
import { injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/assessment/i18n/scope";

@Component({
    selector: "assessment-linked-overview",
    templateUrl: "./assessment-linked-overview.component.html",
    styleUrls: ["./assessment-linked-overview.component.scss"],
    imports: [SharedModule],
    viewProviders: [provideTranslationScope(scope)],
})
export class AssessmentLinkedOverviewComponent implements OnInit {
    private assessmentDataService = inject(AssessmentDataService);
    private findingReportDataService = inject(FindingReportDataService);
    private tabService = inject(TabService);
    private pages = inject(Pages);
    private _promptService = inject(CerrixPromptService);
    private _toastr = inject(ToastrService);
    private _translation = injectTranslation(scope);

    @Input() parentGuid: string;

    @Input() canAdd = false;
    @Input() canDelete = false;

    genericListConfig: GenericListConfig;

    hasAccessChecks = {};
    accessCheckInProgress = false;

    async ngOnInit(): Promise<void> {
        // This component's template renders no `| t` pipe, so nothing else loads the
        // scope. Load it before building the config the child list reads on its own init.
        await this._translation.ready();
        this.loadConfig();
    }

    private loadConfig() {
        const t = (key: string, params?: Record<string, unknown>) =>
            this._translation.t(`linkedOverview.${key}`, params);

        this.genericListConfig = {
            idProp: "guid",
            uiidAdd: "assessments-addbutton",
            uiidDelete: "assessments-deletebutton",
            name: t("title"),
            isSortable: false,
            emptyMessage: t("emptyMessage"),
            allowAdd: this.canAdd,
            allowDelete: this.canDelete,
            allowEdit: this.canAdd,
            dataMethod: this.getAssessments.bind(this),
            fields: [
                {
                    fieldType: GenericListFieldType.Text,
                    fieldName: "identifier",
                    prettyName: t("identifierHeader"),
                },
                {
                    fieldType: GenericListFieldType.Text,
                    fieldName: "assessmentTypeName",
                    prettyName: t("assessmentTypeHeader"),
                },
                {
                    fieldType: GenericListFieldType.Text,
                    fieldName: "overallScore",
                    prettyName: t("overallScoreHeader"),
                },
                {
                    fieldType: GenericListFieldType.Text,
                    fieldName: "overallScoreColor",
                    prettyName: t("overallScoreColorHeader"),
                    hideInOverview: true,
                },
                {
                    fieldType: GenericListFieldType.Text,
                    fieldName: "assessorName",
                    prettyName: t("assessorHeader"),
                },
                {
                    fieldType: GenericListFieldType.Text,
                    fieldName: "assessorOrganizationName",
                    prettyName: t("organizationAssessorHeader"),
                },
                {
                    fieldType: GenericListFieldType.Number,
                    fieldName: "numberOfLinkedMois",
                    prettyName: t("linkedMoisHeader"),
                },
                {
                    fieldType: GenericListFieldType.Date,
                    fieldName: "assessmentDate",
                    prettyName: t("assessmentDateHeader"),
                },
            ],
            rendererConfig: [
                {
                    textColumn: "assessmentDate",
                    type: RendererType.Default,
                    formatType: FormatType.DateFormat,
                },
                {
                    textColumn: "overallScore",
                    actionColumn: "overallScoreColor",
                    hideActionColumn: true,
                    type: RendererType.Score,
                },
            ],
            addOverride: () => {
                this.tabService.generateTab(this.pages.AssessmentDetail, null, t("newTabTitle"), {
                    parentGuid: this.parentGuid,
                });
            },
            editOverride: async (model: LinkedAssessmentModel) => {
                this.openAssessment(model);
            },
            delete: async (model) => {
                if (
                    await this._promptService
                        .confirm(
                            t("deleteTitle"),
                            t("deleteConfirmBody", { typeName: model.assessmentTypeName })
                        )
                        .toPromise()
                ) {
                    return firstValueFrom(this.assessmentDataService
                        .deleteAssessment(model.guid)
                        .pipe(map(() => true)));
                }

                return false;
            },
        };
    }

    private getAssessments() {
        return this.findingReportDataService.getLinkedAssessments(this.parentGuid);
    }

    openAssessment(item: LinkedAssessmentModel): void {
        const hasAccess = this.hasAccessChecks[item.guid];
        if (hasAccess == null && !this.accessCheckInProgress) {
            this.accessCheckInProgress = true;

            const loadingPrompt = this._promptService.loader(
                this._translation.t("linkedOverview.checkingRights")
            );

            this.assessmentDataService
                .hasAccessToAssessment(this.parentGuid, item.guid)
                .pipe(finalize(() => loadingPrompt.close()))
                .subscribe((x) => {
                    this.hasAccessChecks[item.guid] = x;

                    this.openTab(item, x);
                    this.accessCheckInProgress = false;
                });
        } else {
            this.openTab(item, hasAccess);
        }
    }

    private openTab(model: LinkedAssessmentModel, hasAccess: boolean): void {
        if (hasAccess === true) {
            this.tabService.generateTab(
                this.pages.AssessmentDetail,
                model.guid,
                this._translation.t("assessmentTabTitle", {
                    // Pass the identifier as a string: ICU would group-format a number.
                    identifier: `${+model.identifier}`,
                    typeName: model.assessmentTypeName,
                }),
                { parentGuid: this.parentGuid }
            );
        } else {
            this._toastr.warning(
                this._translation.t("linkedOverview.notEnoughRights")
            );
        }
    }
}
